<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { apiBaseURL } from '@/config/constants'
import { formatEpoch, formatMoneyInt } from '@/utils/format'
import type { PortalUserProfile, PortalUserRow } from '@/api/modules/portalUsers'
import {
  createPortalUser,
  createPortalUserLoanOrder,
  deletePortalUser,
  fetchPortalUserProfile,
  fetchPortalUsers,
  patchPortalUserPassword,
  patchPortalUserPhone,
  patchPortalUserStatus,
  patchPortalUserUserinfo,
  patchPortalUserWithdrawalCode,
  patchPortalUserVerifyFeeBanner,
  repairPortalUserLoanOrder,
  sendPortalUserWithdrawalSms,
} from '@/api/modules/portalUsers'

const q = reactive({ keyword: '', nameKeyword: '', pageUi: 1, size: 10 })
const loading = ref(false)
const rows = ref<PortalUserRow[]>([])
const total = ref(0)

const addDlg = ref(false)
const addPhone = ref('')
const addPassword = ref('')
const addSaving = ref(false)

const profileDlg = ref(false)
const profileLoading = ref(false)
const profile = ref<PortalUserProfile | null>(null)
const verifyBannerSaving = ref(false)
const userinfoSaving = ref(false)

const loanForm = reactive({
  money: 80000,
  months: 12,
  allowIncomplete: false,
  feeDraft: true,
})
const loanSubmitting = ref(false)

function coerceUserinfoRecord(raw: unknown): Record<string, unknown> | null {
  let r: unknown = raw
  if (r == null) return null
  if (typeof r === 'string') {
    const s = r.trim()
    if (!s) return null
    try {
      r = JSON.parse(s) as unknown
    } catch {
      return null
    }
  }
  if (typeof r !== 'object' || r === null || Array.isArray(r)) return null
  return r as Record<string, unknown>
}

const profileUi = computed((): Record<string, unknown> | null => {
  return coerceUserinfoRecord(profile.value?.userinfo as unknown)
})

/** 与后端 PATCH 白名单一致的可编辑字段（camelCase） */
const USERINFO_EDIT_ROWS = [
  { key: 'name', label: '姓名', legacy: ['name'] as const },
  { key: 'usercard', label: '身份证号', legacy: ['usercard'] as const },
  { key: 'cardphoto1', label: '身份证正面（路径或 URL）', legacy: ['cardphoto1', 'cardphoto_1'] as const, photo: true },
  { key: 'cardphoto2', label: '身份证反面（路径或 URL）', legacy: ['cardphoto2', 'cardphoto_2'] as const, photo: true },
  { key: 'cardphoto3', label: '第三方证件（路径或 URL）', legacy: ['cardphoto3', 'cardphoto_3'] as const, photo: true },
  { key: 'addressSsq', label: '户籍省/市/区', legacy: ['addressSsq', 'address_ssq', 'addess_ssq'] as const },
  { key: 'addressMore', label: '户籍详细地址', legacy: ['addressMore', 'address_more', 'addess_more'] as const },
  { key: 'dwname', label: '公司名称', legacy: ['dwname'] as const },
  {
    key: 'dwAddressSsq',
    label: '公司省/市/区',
    legacy: ['dwAddressSsq', 'dw_address_ssq', 'dwaddess_ssq'] as const,
  },
  {
    key: 'dwAddressMore',
    label: '公司详细地址',
    legacy: ['dwAddressMore', 'dw_address_more', 'dwaddess_more', 'dwaddressMore'] as const,
  },
  { key: 'position', label: '职位', legacy: ['position'] as const },
  { key: 'workyears', label: '工作年限', legacy: ['workyears'] as const },
  { key: 'dwphone', label: '公司电话', legacy: ['dwphone'] as const },
  { key: 'dwysr', label: '月收入', legacy: ['dwysr'] as const },
  { key: 'bankname', label: '银行名称', legacy: ['bankname'] as const },
  { key: 'bankcard', label: '银行卡号', legacy: ['bankcard'] as const },
  { key: 'iban', label: 'IBAN', legacy: ['iban'] as const },
  { key: 'alipayNum', label: '支付宝账号', legacy: ['alipayNum', 'alipay_num'] as const },
  { key: 'alipay', label: '支付宝（标签）', legacy: ['alipay'] as const },
  { key: 'wechat', label: '微信（标签）', legacy: ['wechat'] as const },
  { key: 'sesame', label: '芝麻信用分', legacy: ['sesame'] as const },
  { key: 'zmxyf', label: '芝麻信用扩展（zmxyf）', legacy: ['zmxyf'] as const },
  { key: 'education', label: '学历', legacy: ['education'] as const },
  { key: 'phone', label: '资料手机号', legacy: ['phone'] as const },
  { key: 'email', label: '邮箱', legacy: ['email'] as const },
  { key: 'personname1', label: '紧急联系人 1 · 姓名', legacy: ['personname1', 'personname_1'] as const },
  { key: 'persongx1', label: '紧急联系人 1 · 关系', legacy: ['persongx1', 'persongx_1'] as const },
  { key: 'personphone1', label: '紧急联系人 1 · 电话', legacy: ['personphone1', 'personphone_1'] as const },
  { key: 'personname2', label: '紧急联系人 2 · 姓名', legacy: ['personname2', 'personname_2'] as const },
  { key: 'persongx2', label: '紧急联系人 2 · 关系', legacy: ['persongx2', 'persongx_2'] as const },
  { key: 'personphone2', label: '紧急联系人 2 · 电话', legacy: ['personphone2', 'personphone_2'] as const },
  { key: 'qm', label: '签名/备注（qm）', legacy: ['qm'] as const, textarea: true },
] as const

type UserinfoEditRow = (typeof USERINFO_EDIT_ROWS)[number]

function photoRow(row: UserinfoEditRow): boolean {
  return 'photo' in row && !!row.photo
}

function textareaRow(row: UserinfoEditRow): boolean {
  return 'textarea' in row && !!row.textarea
}

function userinfoFieldFullWidth(row: UserinfoEditRow): boolean {
  return photoRow(row) || textareaRow(row)
}

const USERINFO_INT_PATCH_KEYS = new Set(['alipay', 'wechat', 'sesame', 'phone'])

const userinfoDraft = reactive<Record<string, string>>({})
let userinfoDraftInitialJson = ''

function hydrateUserinfoDraft(ui: Record<string, unknown> | null) {
  for (const row of USERINFO_EDIT_ROWS) {
    const v = ui ? legacyKeysFold(ui, row.legacy) : undefined
    userinfoDraft[row.key] = v == null || v === '' ? '' : String(v)
  }
  userinfoDraftInitialJson = JSON.stringify({ ...userinfoDraft })
}

function buildUserinfoPatch(): Record<string, unknown> {
  let initial: Record<string, string> = {}
  try {
    initial = JSON.parse(userinfoDraftInitialJson) as Record<string, string>
  } catch {
    initial = {}
  }
  const patch: Record<string, unknown> = {}
  for (const row of USERINFO_EDIT_ROWS) {
    const k = row.key
    const cur = (userinfoDraft[k] ?? '').trim()
    const prev = (initial[k] ?? '').trim()
    if (cur === prev)
      continue
    if (USERINFO_INT_PATCH_KEYS.has(k)) {
      if (cur === '') {
        patch[k] = null
      }
      else {
        const n = Number(cur)
        patch[k] = Number.isFinite(n) ? n : null
      }
    }
    else {
      patch[k] = cur === '' ? null : cur
    }
  }
  return patch
}

function fmtEpoch(sec?: number | null) {
  return formatEpoch(sec, '—')
}

/** 按给定键顺序取第一个非空（兼容 camelCase / 库里历史下划线别名） */
function legacyKeysFold(ui: Record<string, unknown>, keys: readonly string[]): unknown {
  for (const k of keys) {
    const v = ui[k]
    if (v !== undefined && v !== null && v !== '') return v
  }
  return undefined
}

function displayUserinfo(ui: Record<string, unknown> | null | undefined, keys: readonly string[]): string {
  if (!ui) return '—'
  const v = legacyKeysFold(ui, keys)
  if (v === undefined || v === null || v === '') return '—'
  return String(v)
}

function resolveUploadUrl(pathOrUrl: unknown): string {
  if (pathOrUrl == null) return ''
  let s = String(pathOrUrl).trim()
  if (!s || s === '—') return ''
  /** SPA 曾误把 blob:/data: 前加 `/`，数据库存成 `/blob:…` 会导致图片 404 */
  if (/^\/blob:/i.test(s)) s = s.slice(1)
  if (/^\/data:/i.test(s)) s = s.slice(1)
  if (/^\/https?:\/\//i.test(s)) s = s.slice(1)
  if (/^blob:/i.test(s) || /^data:/i.test(s)) return s
  if (/^https?:\/\//i.test(s)) return s
  const base = apiBaseURL().replace(/\/$/, '')
  if (s.startsWith('/')) return `${base}${s}`
  return `${base}/${s}`
}

function formatScalarForVerification(v: unknown, depth = 0): string {
  if (v === null || v === undefined) return '—'
  if (depth > 4) return '…'
  if (typeof v === 'object') {
    if (Array.isArray(v)) {
      if (v.length === 0) return '—'
      return v.map((item, i) => `${i + 1}) ${formatScalarForVerification(item, depth + 1)}`).join(' · ')
    }
    const o = v as Record<string, unknown>
    const keys = Object.keys(o)
    if (keys.length === 0) return '—'
    return keys.map((k) => `${k}：${formatScalarForVerification(o[k], depth + 1)}`).join('； ')
  }
  return String(v)
}

const KNOWN_USERINFO_KEY_SET = new Set<string>([
  'id',
  'user',
  ...USERINFO_EDIT_ROWS.flatMap((r) => [r.key, ...r.legacy]),
])

function extraUserinfoKeys(ui: Record<string, unknown>): string[] {
  return Object.keys(ui)
    .filter((k) => !KNOWN_USERINFO_KEY_SET.has(k))
    .sort((a, b) => a.localeCompare(b))
}

function statusLabel(s?: number | null) {
  return Number(s) === 1 ? '允许登录' : '禁止登录'
}

async function load() {
  loading.value = true
  try {
    const { data } = await fetchPortalUsers(
      q.keyword || undefined,
      Math.max(0, q.pageUi - 1),
      q.size,
      q.nameKeyword || undefined,
    )
    rows.value = data.content ?? []
    total.value = data.totalElements ?? 0
  } finally {
    loading.value = false
  }
}

function search() {
  q.pageUi = 1
  load()
}

function onPage() {
  load()
}

function onSize(s: number) {
  q.size = s
  q.pageUi = 1
  load()
}

function openAdd() {
  addPhone.value = ''
  addPassword.value = ''
  addDlg.value = true
}

async function submitAdd() {
  const ph = addPhone.value.trim()
  if (ph.length < 4) {
    ElMessage.warning('手机号/账号至少 4 位')
    return
  }
  if (!addPassword.value || addPassword.value.length < 6) {
    ElMessage.warning('密码至少 6 位（与注册规则一致，最长 16 位）')
    return
  }
  if (addPassword.value.length > 16) {
    ElMessage.warning('密码最长 16 位')
    return
  }
  addSaving.value = true
  try {
    await createPortalUser(ph, addPassword.value)
    ElMessage.success('用户已添加')
    addDlg.value = false
    search()
  } catch {
    /* interceptor */
  } finally {
    addSaving.value = false
  }
}

async function openProfile(row: PortalUserRow) {
  profile.value = null
  profileDlg.value = true
  profileLoading.value = true
  try {
    const { data } = await fetchPortalUserProfile(row.id)
    profile.value = data
    hydrateUserinfoDraft(coerceUserinfoRecord(data.userinfo))
  } finally {
    profileLoading.value = false
  }
}

async function submitSaveUserinfo() {
  const id = profile.value?.user?.id
  if (id == null)
    return
  const patch = buildUserinfoPatch()
  if (Object.keys(patch).length === 0) {
    ElMessage.info('资料未变更')
    return
  }
  userinfoSaving.value = true
  try {
    const { data } = await patchPortalUserUserinfo(id, patch)
    profile.value = data
    hydrateUserinfoDraft(coerceUserinfoRecord(data.userinfo))
    ElMessage.success('资料已保存')
    load()
  }
  catch {
    /* axios */
  }
  finally {
    userinfoSaving.value = false
  }
}

async function toggleStatus(row: PortalUserRow) {
  const next: 0 | 1 = Number(row.status) === 1 ? 0 : 1
  const tip = next === 0 ? '禁止该用户登录？' : '允许该用户登录？'
  try {
    await ElMessageBox.confirm(tip, '状态', { type: 'warning' })
    await patchPortalUserStatus(row.id, next)
    ElMessage.success('已更新')
    load()
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

async function onChangePassword(row: PortalUserRow) {
  try {
    const { value } = await ElMessageBox.prompt('新密码（至少 6 位）', `修改密码 · ID ${row.id}`, {
      inputType: 'password',
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      inputValidator: (v) => {
        if (!v || v.length < 6) return '至少 6 位'
        return true
      },
    })
    await patchPortalUserPassword(row.id, value)
    ElMessage.success('密码已修改（SHA1(MD5) 与前台登录一致）')
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

async function onChangeWithdrawalCode(row: PortalUserRow) {
  try {
    const { value } = await ElMessageBox.prompt(
      '新提现码（6-10 位数字，数据库字段 discode）',
      `提现码 · ID ${row.id}`,
      {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        inputValidator: (v) => {
          if (!v || !/^\d{6,10}$/.test(v)) return '必须是 6-10 位数字'
          return true
        },
      },
    )
    await patchPortalUserWithdrawalCode(row.id, value)
    ElMessage.success('提现码已更新')
    load()
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

async function onChangePhone(row: PortalUserRow) {
  try {
    const { value } = await ElMessageBox.prompt('新手机号/登录账号', `修改账号 · ID ${row.id}`, {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      inputValidator: (v) => {
        if (!v || v.trim().length < 4) return '至少 4 位'
        return true
      },
    })
    await patchPortalUserPhone(row.id, value.trim())
    ElMessage.success('已更新；若存在 userinfo，user 字段将同步')
    load()
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

async function onSendWithdrawalSms(row: PortalUserRow) {
  if (row.discode == null) {
    ElMessage.warning('请先设置提现码')
    return
  }
  try {
    await ElMessageBox.confirm(
      `向 ${row.phone} 发送提现码短信？（使用 Smsbao 配置，短信内容包含"尊敬的客户，您的提现码为："+ 码）`,
      '发送短信',
      { type: 'info' },
    )
    await sendPortalUserWithdrawalSms(row.id)
    ElMessage.success('已请求发送（请检查 Smsbao 响应及手机）')
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

async function onDelete(row: PortalUserRow) {
  try {
    await ElMessageBox.confirm(
      `确认删除用户"${row.phone}"（ID ${row.id}）？关联的 userinfo 行也将被删除（若有）。`,
      '确认删除',
      { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' },
    )
    await deletePortalUser(row.id)
    ElMessage.success('已删除')
    load()
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

const repairMsgs: Record<string, string> = {
  already_has_active_order: '用户已有借款订单，无需修复',
  user_has_no_phone: '用户无手机号',
  restore_failed: '从回收站恢复失败',
  create_missing_loan_id: '修复失败：未返回订单 ID',
}

async function onRepairLoanOrder(row: PortalUserRow) {
  try {
    await ElMessageBox.confirm(
      `一键修复"${row.phone}"的借款订单？\n优先级：从回收站恢复；若无，则根据钱包余额或近期提现金额创建已审核订单。`,
      '修复订单',
      { type: 'warning', confirmButtonText: '修复', cancelButtonText: '取消' },
    )
    const { data } = await repairPortalUserLoanOrder(row.id)
    if (Number(data?.status) !== 1) {
      const code = String(data?.msg ?? '')
      ElMessage.error(repairMsgs[code] ?? (code || 'Repair failed'))
      return
    }
    const action = data.action
    if (action === 'none') {
      ElMessage.info(repairMsgs.already_has_active_order)
    } else if (action === 'restore') {
      ElMessage.success(`已从回收站恢复订单 ${data.orderNum ?? data.loanId ?? ''}`)
    } else {
      ElMessage.success(`已创建订单 ${data.orderNum ?? data.loanId ?? ''}`)
    }
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

async function toggleVerifyBanner(on: boolean) {
  const id = profile.value?.user?.id
  if (id == null)
    return
  verifyBannerSaving.value = true
  try {
    const { data } = await patchPortalUserVerifyFeeBanner(id, on)
    if (profile.value) {
      profile.value = {
        ...profile.value,
        user: { ...profile.value.user, showVerifyFeeBanner: !!data.showVerifyFeeBanner },
      }
    }
    ElMessage.success(on ? '审核费横幅已开启（需在支付设置中开启全局开关）' : '已为该用户关闭审核费横幅')
    load()
  }
  finally {
    verifyBannerSaving.value = false
  }
}

async function submitProxyLoan() {
  const id = profile.value?.user?.id
  if (id == null)
    return
  loanSubmitting.value = true
  try {
    const { data } = await createPortalUserLoanOrder(id, {
      money: loanForm.money,
      months: loanForm.months,
      allowIncompleteProfile: loanForm.allowIncomplete,
      createVerificationFeeDraft: loanForm.feeDraft,
    })
    if (Number(data?.status) !== 1) {
      const m = String(data?.msg ?? 'Creation failed')
      const map: Record<string, string> = {
        profile_missing: '用户无 userinfo，无法创建订单',
        profile_incomplete_for_loan: '资料未通过完整性校验。请开启"资料不完整也创建订单"或让用户完善资料。',
        user_has_no_phone: '用户无手机号',
        data_integrity_violation: '数据冲突或重复，订单可能已存在',
        database_error: '数据库错误',
        unexpected_error: '服务器错误',
      }
      ElMessage.error(map[m] ?? m)
      return
    }
    ElMessage.success(
      `借款订单已创建 ${data.orderNum ?? ''}${data.loanId != null ? ` (loanId ${data.loanId})` : ''}。可在「借款管理」中查看。`,
    )
    load()
  }
  catch {
    /* axios interceptor */
  }
  finally {
    loanSubmitting.value = false
  }
}

onMounted(load)
</script>

<template>
  <PageChrome
    title="前台用户管理"
    description="对标旧版 Admin/User。检索：手机号或信贷订单号（OR）；客户姓名按资料 userinfo.name 模糊。口令 SHA1(MD5)。提现码 discode。"
  >
    <template #actions>
      <el-button type="primary" @click="openAdd">添加用户</el-button>
      <el-button :loading="loading" @click="load">刷新</el-button>
    </template>

    <div class="ep-filter-panel">
      <el-form inline>
        <el-form-item label="手机/订单号">
          <el-input
            v-model="q.keyword"
            clearable
            placeholder="手机号或信贷订单号（模糊 OR）"
            style="width: 236px"
            @keyup.enter="search"
          />
        </el-form-item>
        <el-form-item label="客户姓名">
          <el-input
            v-model="q.nameKeyword"
            clearable
            placeholder="客户姓名（模糊）"
            style="width: 200px"
            @keyup.enter="search"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="search">查询</el-button>
        </el-form-item>
      </el-form>
    </div>

    <el-card shadow="hover" class="ep-table-scope adm-card">
      <el-table v-loading="loading" :data="rows" stripe border size="default">
        <el-table-column prop="id" label="ID" width="88" align="center" />
        <el-table-column prop="phone" label="手机号" min-width="140" show-overflow-tooltip />
        <el-table-column prop="name" label="客户姓名" min-width="120" show-overflow-tooltip />
        <el-table-column label="登录密码" min-width="140" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.loginPassword?.trim() ? row.loginPassword : '—' }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="108" align="center">
          <template #default="{ row }">
            <el-tag :type="Number(row.status) === 1 ? 'success' : 'info'" size="small">
              {{ statusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="注册时间" width="172" align="center">
          <template #default="{ row }">{{ fmtEpoch(row.addtime) }}</template>
        </el-table-column>
        <el-table-column label="提现码" width="120" align="center">
          <template #default="{ row }">
            {{ row.discode != null ? row.discode : '—' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="320" align="center" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" link @click="toggleStatus(row)">
              {{ Number(row.status) === 1 ? '禁止登录' : '允许登录' }}
            </el-button>
            <el-button type="primary" link @click="onChangeWithdrawalCode(row)">修改提现码</el-button>
            <el-button type="primary" link @click="onSendWithdrawalSms(row)">发送提现短信</el-button>
            <el-button type="primary" link @click="openProfile(row)">查看资料</el-button>
            <el-button type="warning" link @click="onRepairLoanOrder(row)">修复订单</el-button>
            <el-button type="primary" link @click="onChangePassword(row)">修改密码</el-button>
            <el-button type="primary" link @click="onChangePhone(row)">修改手机号</el-button>
            <el-button type="danger" link @click="onDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="ep-pager-footer pager">
        <el-pagination
          v-model:current-page="q.pageUi"
          v-model:page-size="q.size"
          :total="total"
          :page-sizes="[10, 15, 30, 50]"
          layout="sizes,prev,pager,next,total"
          background
          @current-change="onPage"
          @size-change="onSize"
        />
      </div>
    </el-card>

    <el-dialog v-model="addDlg" title="添加用户" width="420px" destroy-on-close>
      <el-form label-width="96px">
        <el-form-item label="手机号" required>
          <el-input v-model="addPhone" maxlength="64" autocomplete="off" placeholder="登录账号/手机号" />
        </el-form-item>
        <el-form-item label="密码" required>
          <el-input v-model="addPassword" maxlength="16" type="password" show-password autocomplete="new-password" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDlg = false">取消</el-button>
        <el-button type="primary" :loading="addSaving" @click="submitAdd">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="profileDlg" title="用户资料（user + userinfo）" width="920px" destroy-on-close>
      <div v-loading="profileLoading">
        <template v-if="profile?.user">
          <h4 class="prof-h">user</h4>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="id">{{ profile.user.id }}</el-descriptions-item>
            <el-descriptions-item label="phone">{{ profile.user.phone }}</el-descriptions-item>
            <el-descriptions-item label="登录密码">
              {{ profile.user.loginPassword?.trim() ? profile.user.loginPassword : '—' }}
            </el-descriptions-item>
            <el-descriptions-item label="status">{{ profile.user.status }}</el-descriptions-item>
            <el-descriptions-item label="addtime">{{ fmtEpoch(profile.user.addtime) }}</el-descriptions-item>
            <el-descriptions-item label="discode">{{ profile.user.discode ?? '—' }}</el-descriptions-item>
            <el-descriptions-item label="money">{{ formatMoneyInt(profile.user.money) }}</el-descriptions-item>
            <el-descriptions-item label="addMoney">{{ formatMoneyInt(profile.user.addMoney) }}</el-descriptions-item>
            <el-descriptions-item label="审核费横幅（用户级开关）" :span="2">
              <span class="vf-hint">
                当此开关与「系统设置 ▸ 收款账户 ▸ 审核费横幅」全局开关同时开启时，该用户的「我的」页面会显示引导。
              </span>
              <el-switch
                :model-value="profile.user.showVerifyFeeBanner === true"
                style="vertical-align: middle"
                :loading="verifyBannerSaving"
                inline-prompt
                active-text="开"
                inactive-text="关"
                @change="(v:any) => toggleVerifyBanner(Boolean(v))"
              />
            </el-descriptions-item>
          </el-descriptions>

          <h4 class="prof-h">userinfo（可编辑）</h4>
          <el-alert
            v-if="!profileUi"
            class="prof-userinfo-alert"
            type="info"
            :closable="false"
            show-icon
            title="当前尚无 userinfo 行；填写下方表单并保存后将新建一行，关联到该用户的登录手机号。"
          />
          <el-descriptions v-if="profileUi" :column="2" border size="small" class="prof-desc prof-meta">
            <el-descriptions-item label="资料 ID">{{ displayUserinfo(profileUi, ['id']) }}</el-descriptions-item>
            <el-descriptions-item label="绑定用户">{{ displayUserinfo(profileUi, ['user']) || profile.user.phone }}</el-descriptions-item>
          </el-descriptions>

          <el-form label-width="168px" class="prof-userinfo-form" @submit.prevent>
            <el-row :gutter="12">
              <el-col
                v-for="row in USERINFO_EDIT_ROWS"
                :key="row.key"
                :span="userinfoFieldFullWidth(row) ? 24 : 12"
              >
                <el-form-item :label="row.label">
                  <template v-if="photoRow(row)">
                    <el-input v-model="userinfoDraft[row.key]" clearable placeholder="相对路径或完整 URL" />
                    <div class="prof-photo-stack prof-photo-stack--draft">
                      <el-image
                        v-if="resolveUploadUrl(userinfoDraft[row.key])"
                        class="prof-thumb"
                        :src="resolveUploadUrl(userinfoDraft[row.key])"
                        :preview-src-list="[resolveUploadUrl(userinfoDraft[row.key])].filter(Boolean)"
                        fit="contain"
                        preview-teleported
                      >
                        <template #error>
                          <span class="prof-thumb-fail">加载失败</span>
                        </template>
                      </el-image>
                      <div v-else class="prof-photo-missing">无预览（输入路径后显示）</div>
                    </div>
                  </template>
                  <el-input
                    v-else-if="textareaRow(row)"
                    v-model="userinfoDraft[row.key]"
                    type="textarea"
                    :rows="3"
                    clearable
                  />
                  <el-input v-else v-model="userinfoDraft[row.key]" clearable />
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item>
              <el-button type="primary" :loading="userinfoSaving" @click="submitSaveUserinfo">保存资料</el-button>
            </el-form-item>
          </el-form>

          <template v-if="profileUi && extraUserinfoKeys(profileUi).length">
            <h4 class="prof-h prof-h--sub">其他字段（只读，不在编辑列表中）</h4>
            <el-descriptions :column="2" border size="small" class="prof-desc">
              <el-descriptions-item
                v-for="ek in extraUserinfoKeys(profileUi)"
                :key="ek"
                :label="ek"
                :span="2"
              >
                <div class="prof-extra-val">{{ formatScalarForVerification(profileUi[ek]) }}</div>
              </el-descriptions-item>
            </el-descriptions>
          </template>

          <h4 class="prof-h">代理借款申请</h4>
          <el-alert
            class="prof-loan-alert"
            type="warning"
            :closable="false"
            show-icon
          >
            <template #title>
              <span class="prof-loan-alert__title">用户资料在数据库中已存在，但如果前台签约提交未完成，不会产生订单记录。在此填写本金和期数，生成与前台一致的借款订单（默认状态：待审核/审核费）。可先保存上方资料再创建订单。</span>
            </template>
          </el-alert>
          <el-form label-width="140px" class="prof-loan-form" @submit.prevent>
            <el-form-item label="本金 (AED)">
              <el-input-number
                v-model="loanForm.money"
                :min="1"
                :max="999999999"
                :step="1000"
                controls-position="right"
                style="width: 220px"
              />
            </el-form-item>
            <el-form-item label="期数（月）">
              <el-input-number
                v-model="loanForm.months"
                :min="1"
                :max="120"
                :step="1"
                controls-position="right"
                style="width: 220px"
              />
            </el-form-item>
            <el-form-item label="资料不完整也创建订单">
              <div class="prof-loan-row">
                <el-switch v-model="loanForm.allowIncomplete" />
                <span class="prof-loan-hint">通常关闭；仅当 KYC 未通过后端完整性校验但仍需先下订单时开启</span>
              </div>
            </el-form-item>
            <el-form-item label="生成审核费草稿">
              <div class="prof-loan-row">
                <el-switch v-model="loanForm.feeDraft" />
                <span class="prof-loan-hint">与前台提交一致；会尝试创建审核费支付单，失败时订单仍保留</span>
              </div>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" :loading="loanSubmitting" @click="submitProxyLoan">
                创建借款订单
              </el-button>
            </el-form-item>
          </el-form>
        </template>
      </div>
    </el-dialog>
  </PageChrome>
</template>

<style scoped lang="scss">
.adm-card {
  border-radius: var(--ep-radius-lg) !important;
}
.pager {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
.vf-hint {
  display: block;
  font-size: 12px;
  color: #909399;
  line-height: 1.45;
  margin-bottom: 8px;
  max-width: 520px;
}
.prof-h {
  margin: 16px 0 8px;
  font-size: 14px;
  color: #303133;
}
.prof-h:first-of-type {
  margin-top: 0;
}
.prof-empty {
  color: #909399;
  font-size: 13px;
}
.prof-desc {
  margin-top: 4px;
}
.prof-desc :deep(.el-descriptions__label) {
  width: 150px;
  color: #606266;
  font-weight: 500;
}
.prof-multiline,
.prof-extra-val {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
  font-size: 13px;
}
.prof-photo-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}
.prof-photo-path {
  font-size: 12px;
  color: #606266;
  word-break: break-all;
  line-height: 1.35;
}
.prof-photo-missing {
  font-size: 12px;
  color: #909399;
}
.prof-thumb {
  width: 200px;
  height: 120px;
  border-radius: 6px;
  border: 1px solid var(--el-border-color);
  background: #fafafa;
}
.prof-thumb-fail {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  font-size: 12px;
  color: #909399;
}
.prof-h--sub {
  margin-top: 20px;
  font-size: 13px;
  color: #606266;
}
.prof-loan-alert {
  margin: 10px 0 14px;
}
.prof-loan-alert__title {
  font-size: 13px;
  line-height: 1.55;
  font-weight: 500;
}
.prof-loan-form {
  max-width: 560px;
  margin-bottom: 8px;
}
.prof-loan-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.prof-loan-hint {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  max-width: 380px;
}
</style>
